  import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { UnifiedScheduleView, GanttTimeline, ScheduleHeader } from '../components/scheduling';
import AIScheduleAssistant from '../components/scheduling/AIScheduleAssistant';
import ActivityBottomPanel from '../components/scheduling/ActivityBottomPanel';
import LayoutTemplatesMenu from '../components/scheduling/LayoutTemplatesMenu';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import { useIsMobile } from '../hooks/useIsMobile';
import MobileScheduleView from '../mobile/MobileScheduleView';
import { mergeActivityList } from '../lib/merge_activities';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { format, addWeeks } from 'date-fns';
import { toast } from 'sonner';
import api from '../lib/api';
import { parsedRowToActivity } from '../lib/activity_clipboard';
import { 
  Building2, 
  CalendarDays, 
  Maximize2,
  Minimize2,
  Info,
  ListTree,
  GripVertical,
  Flag,
  GitBranch,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Clock,
  ArrowRight,
  Columns,
  PanelLeft,
  BarChart3,
  Filter,
  X,
  Eye,
  EyeOff,
  Undo2,
  Copy,
  ClipboardPaste,
  Loader2
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { cn } from '../lib/utils';
import { Action } from '../lib/auth';

const SchedulingScreen = () => {
  const { activeProject, activeSchedule, can } = useProject();
  const isMobile = useIsMobile();

  // Single source of truth — store ONLY the id (a primitive that cannot
  // drift). The activity object is derived from `allActivities` via
  // useMemo below. When a save fires and `allActivities` refetches
  // (handleActivityChange → setRefreshKey → effect at :284), the
  // derived `selectedActivity` automatically reflects the fresh server
  // state. This eliminates the "panel doesn't sync without hard refresh"
  // bug — the snapshot pattern that caused it cannot exist here.
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const [isActivityPanelOpen, setIsActivityPanelOpen] = useState(false);

  // Layer 3 — persist selectedActivityId per-schedule across browser refresh.
  // Two effects: restore on schedule load, persist on change.
  // sessionStorage (not localStorage) so two tabs of different schedules
  // don't fight over the same selection slot.
  const selectionStorageKey = activeSchedule?.id
    ? `epicplan.selectedActivityId.${activeSchedule.id}`
    : null;
  useEffect(() => {
    if (!selectionStorageKey) return;
    try {
      const stored = window.sessionStorage.getItem(selectionStorageKey);
      if (stored) {
        setSelectedActivityId(stored);
        setIsActivityPanelOpen(true);
      }
    } catch {
      // sessionStorage unavailable (private mode etc.) — silent fallback.
    }
    // Only restore on schedule load. The next effect handles writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionStorageKey]);
  useEffect(() => {
    if (!selectionStorageKey) return;
    try {
      if (selectedActivityId) {
        window.sessionStorage.setItem(selectionStorageKey, selectedActivityId);
      } else {
        window.sessionStorage.removeItem(selectionStorageKey);
      }
    } catch {
      // Storage write failed — in-memory selection still works.
    }
  }, [selectionStorageKey, selectedActivityId]);
  
  // Full screen mode state
  const [isFullScreen, setIsFullScreen] = useState(false);
  
  // Resources and calendars for activity edit dialog
  const [projectResources, setProjectResources] = useState([]);
  const [projectCalendars, setProjectCalendars] = useState([]);
  
  // Refresh trigger for child components — bumped for FULL refetches
  // (handleActivityChange, schedule swap, etc.).
  const [refreshKey, setRefreshKey] = useState(0);

  // Comments: entity_id -> count for row badges, plus a signal that
  // deep-opens the bottom panel's Comments tab on badge click.
  const [commentCounts, setCommentCounts] = useState({});
  const [openCommentsSignal, setOpenCommentsSignal] = useState(0);
  // Separate signal for fast-path merge updates. The activity table reads
  // merged state via allActivities directly; the Gantt does NOT share
  // that state (it fetches its own activities), so it needs a separate
  // signal to know "data changed, please re-pull" WITHOUT triggering the
  // wider refreshKey-driven full-refresh cascade.
  const [mergedRevision, setMergedRevision] = useState(0);
  
  // Shared expanded nodes state for Activity Grid and Gantt alignment
  const [sharedExpandedNodes, setSharedExpandedNodes] = useState({});
  
  // Mode state for Baseline/Scenario modes
  const [scheduleMode, setScheduleMode] = useState('normal'); // 'normal' | 'baseline' | 'scenario'
  const [selectedBaselineId, setSelectedBaselineId] = useState(null);
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);
  
  // Baseline comparison data (loaded in-place)
  const [baselineComparisonData, setBaselineComparisonData] = useState(null);
  
  // Baseline columns data - for displaying baseline dates in schedule view (separate from baseline mode)
  const [selectedBaselineForColumns, setSelectedBaselineForColumns] = useState(null);
  const [baselineColumnsData, setBaselineColumnsData] = useState(null);
  
  // Scenario comparison data (loaded in-place)
  const [scenarioData, setScenarioData] = useState(null);
  
  // Look-Ahead Filter state (visibility filter only - no data mutation).
  // Persisted to localStorage so the user's view-state survives a refresh.
  // null = show all, 3 = 3 weeks, 6 = 6 weeks. See plan kind-questing-storm.md
  // Layer 6 — matches the long-standing CLAUDE.md docstring claim that filters
  // are persisted (which had drifted out of truth before this commit).
  const [lookAheadWeeks, setLookAheadWeeks] = useLocalStorageState(
    'epicplan.scheduling.lookAheadWeeks', null,
  );

  // Date Range Filter state (visibility filter only)
  const [dateRangeFilter, setDateRangeFilter] = useLocalStorageState(
    'epicplan.scheduling.dateRangeFilter', { from: null, to: null },
  );

  // Status Filter state — 'all' | 'not_started' | 'in_progress' | 'completed'
  const [statusFilter, setStatusFilter] = useLocalStorageState(
    'epicplan.scheduling.statusFilter', 'all',
  );

  // Activity text search filter (ID or name)
  const [activitySearch, setActivitySearch] = useLocalStorageState(
    'epicplan.scheduling.activitySearch', '',
  );

  // All activities for the current schedule — used by ActivityBottomPanel Dependencies tab
  const [allActivities, setAllActivities] = useState([]);

  // WBS Copy/Paste state
  const [selectedWbsNode, setSelectedWbsNode] = useState(null);
  const [clipboardWbs, setClipboardWbs] = useState(null);
  const [showPasteOptions, setShowPasteOptions] = useState(false);
  const [pasteTarget, setPasteTarget] = useState(null);
  const [pasteIncludeActivities, setPasteIncludeActivities] = useState(true);
  const [pasteCopyRelationships, setPasteCopyRelationships] = useState(true);
  const [isPasting, setIsPasting] = useState(false);

  // Critical path filter (persisted, see Layer 6 above)
  const [criticalPathOnly, setCriticalPathOnly] = useLocalStorageState(
    'epicplan.scheduling.criticalPathOnly', false,
  );

  // ============================================================
  // Layout Templates — lifted column/timeline state
  // ============================================================
  // Default column widths mirror UnifiedScheduleView's DEFAULT_COLUMN_WIDTHS
  const LAYOUT_DEFAULT_COLUMN_WIDTHS = {
    links: 28, activityId: 72, activityName: 180, resources: 36,
    baselineStart: 85, currentStart: 90, baselineFinish: 85, currentFinish: 90,
    expectedFinish: 95, duration: 50, progress: 40, float: 50, status: 70
  };
  const [columnWidths, setColumnWidths] = useState(LAYOUT_DEFAULT_COLUMN_WIDTHS);
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [timeScale, setTimeScale] = useState('month');

  // Layout templates state
  const [layoutTemplates, setLayoutTemplates] = useState([]);
  const [defaultTemplateApplied, setDefaultTemplateApplied] = useState(false);
  
  // Layout state: side-by-side only
  const [layoutMode] = useState('side-by-side');
  
  // Resizable pane state for side-by-side layout
  const containerRef = useRef(null);
  const [scheduleWidth, setScheduleWidth] = useState(() => {
    const saved = localStorage.getItem('scheduleWidth');
    return saved ? parseInt(saved) : 50; // percentage
  });
  const [isResizingHorizontal, setIsResizingHorizontal] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Scroll synchronization refs
  const scheduleScrollRef = useRef(null);
  const ganttScrollRef = useRef(null);
  const isScrollingSyncRef = useRef(false);
  
  // Session-level undo stack (lifted up for global access)
  const [undoStack, setUndoStack] = useState([]);
  const [isUndoing, setIsUndoing] = useState(false);
  
  // Global undo handler - processes the most recent change in the stack.
  // Two entry shapes:
  //   - Single-activity edit (legacy): { activityId, field, oldValue, newValue, displayName }
  //   - Bulk edit:                     { type: 'bulk', field, edits: [{activity_id, old_value}], ... }
  // Bulk undo dispatches one PATCH /activities/bulk per distinct old_value
  // group, which keeps the round-trip property the backend test asserts.
  const handleGlobalUndo = useCallback(async () => {
    if (!undoStack || undoStack.length === 0 || isUndoing) return;

    const lastChange = undoStack[0];

    setIsUndoing(true);
    try {
      if (lastChange?.type === 'bulk') {
        if (!Array.isArray(lastChange.edits) || lastChange.edits.length === 0) {
          toast.error('Invalid bulk undo data');
          return;
        }
        // Group activity IDs by their pre-bulk old_value so we issue one
        // bulk PATCH per group. Most realistic case: all rows had the same
        // prior value → 1 group → 1 call. Worst case: N groups → N calls.
        const groups = new Map();
        for (const e of lastChange.edits) {
          const key = JSON.stringify(e.old_value ?? null);
          if (!groups.has(key)) groups.set(key, { value: e.old_value ?? null, ids: [] });
          groups.get(key).ids.push(e.activity_id);
        }
        for (const { value, ids } of groups.values()) {
          await api.patch('/activities/bulk', {
            activity_ids: ids,
            field: lastChange.field,
            value,
            comment: `Undo: bulk ${lastChange.displayName} (${ids.length} rows)`,
          });
        }
        setUndoStack(prev => prev.slice(1));
        toast.success(`Undone: bulk ${lastChange.displayName} on ${lastChange.activityCount ?? lastChange.edits.length} rows`);
        setRefreshKey(prev => prev + 1);
        return;
      }

      // Legacy single-activity undo path
      if (!lastChange?.activityId) {
        toast.error('Invalid undo data');
        return;
      }
      await api.put(`/activities/${lastChange.activityId}`, {
        [lastChange.field]: lastChange.oldValue,
        comment: `Undo: ${lastChange.displayName} reverted from "${lastChange.newValue}" to "${lastChange.oldValue}"`
      });
      setUndoStack(prev => prev.slice(1));
      toast.success(`Undone: ${lastChange.displayName}`);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error(apiErrorMessage(error, 'Failed to undo change'));
    } finally {
      setIsUndoing(false);
    }
  }, [undoStack, isUndoing]);

  // Fetch project resources and calendars for activity edit dialog
  useEffect(() => {
    const fetchResourcesAndCalendars = async () => {
      if (!activeProject?.id) return;

      try {
        // Fetch resources — backend is schedule-scoped, use active schedule
        const scheduleId = activeSchedule?.id || activeProject?.active_schedule_id;
        if (scheduleId) {
          const resourcesResponse = await api.get(`/resources/schedule/${scheduleId}`);
          setProjectResources(resourcesResponse.data || []);
        } else {
          setProjectResources([]);
        }
      } catch (error) {
        console.error('Failed to fetch resources:', error);
        setProjectResources([]);
      }
      
      try {
        // Fetch calendars
        const calendarsResponse = await api.get(`/calendars/project/${activeProject.id}`);
        setProjectCalendars(calendarsResponse.data || []);
      } catch (error) {
        console.error('Failed to fetch calendars:', error);
        setProjectCalendars([]);
      }
    };
    
    fetchResourcesAndCalendars();
  }, [activeProject?.id, activeSchedule?.id]);

  // Fetch all activities for the current schedule — used by ActivityBottomPanel Dependencies tab fallback
  useEffect(() => {
    const fetchAllActivities = async () => {
      if (!activeSchedule?.id) {
        setAllActivities([]);
        return;
      }
      try {
        const response = await api.get(`/activities/schedule/${activeSchedule.id}`);
        // Surgical merge — Layer 7a. Reuse prior activity references where
        // the row's data is unchanged so downstream consumers (the bottom
        // panel, the Gantt) can short-circuit when nothing relevant moved.
        setAllActivities(prev => mergeActivityList(prev, response.data || []));
      } catch (error) {
        console.error('Failed to fetch all activities:', error);
        setAllActivities([]);
      }
    };
    fetchAllActivities();
  }, [activeSchedule?.id, refreshKey]);

  // Single source of truth for "what activities should the table/Gantt
  // render right now". When scenario mode is active, overlay the
  // scenario_activities (server-derived with their own CPM dates and
  // is_modified flags) onto allActivities; otherwise pass through.
  //
  // This memo replaces the parallel fetch + transform that used to
  // live inside UnifiedScheduleView.fetchData. With this single
  // source, the bottom-panel mutations that previously updated only
  // allActivities now propagate to the table in the same React tick
  // via the derivation inside UnifiedScheduleView. Plan
  // zesty-coalescing-clarke.md.
  const visibleActivities = useMemo(() => {
    if (scheduleMode === 'scenario' && scenarioData?.scenario_activities) {
      return scenarioData.scenario_activities.map(scenarioAct => ({
        id: scenarioAct.id,
        activity_id: scenarioAct.activity_id,
        name: scenarioAct.name,
        wbs_id: scenarioAct.wbs_id,
        original_duration: scenarioAct.original_duration,
        remaining_duration: scenarioAct.remaining_duration,
        planned_start: scenarioAct.planned_start,
        planned_finish: scenarioAct.planned_finish,
        early_start: scenarioAct.early_start || scenarioAct.forecast_start,
        early_finish: scenarioAct.early_finish || scenarioAct.forecast_finish,
        late_start: scenarioAct.late_start,
        late_finish: scenarioAct.late_finish,
        total_float: scenarioAct.total_float,
        free_float: scenarioAct.free_float,
        is_critical: scenarioAct.is_critical,
        actual_start: scenarioAct.actual_start,
        actual_finish: scenarioAct.actual_finish,
        percent_complete: scenarioAct.percent_complete || 0,
        status: scenarioAct.status || 'not_started',
        is_scenario_activity: true,
        source_activity_id: scenarioAct.source_activity_id,
        source_duration: scenarioAct.source_duration,
        duration_change: scenarioAct.duration_change,
        is_modified: scenarioAct.is_modified,
      }));
    }
    return allActivities;
  }, [allActivities, scheduleMode, scenarioData]);

  // Global keydown: ESC (fullscreen), Ctrl+C (copy WBS), Ctrl+V (paste WBS)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullScreen) {
        setIsFullScreen(false);
        return;
      }

      // Don't intercept Ctrl+C/V when focus is in a form field — let browser handle native text copy
      const active = document.activeElement;
      const isFormField = active && (
        active.tagName === 'INPUT' ||
        active.tagName === 'TEXTAREA' ||
        active.tagName === 'SELECT' ||
        active.isContentEditable
      );
      if (isFormField) return;

      const ctrl = e.ctrlKey || e.metaKey;
      // WBS copy/paste creates structure (POST /wbs/{id}/copy) => EDIT_STRUCTURE (plan).
      // Gate the shortcuts so view/collaborate can't fire a mutation the backend 403s.
      if (ctrl && e.key === 'c' && selectedWbsNode && can(Action.EDIT_STRUCTURE)) {
        e.preventDefault();
        setClipboardWbs({ node: selectedWbsNode });
        toast.success(`Copied "${selectedWbsNode.name}" — Ctrl+V to paste`);
        return;
      }
      if (ctrl && e.key === 'v' && clipboardWbs && can(Action.EDIT_STRUCTURE)) {
        e.preventDefault();
        setPasteTarget(selectedWbsNode);
        setPasteIncludeActivities(true);
        setPasteCopyRelationships(true);
        setShowPasteOptions(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, selectedWbsNode, clipboardWbs, can]);

  // ============================================================
  // Layout Template helpers
  // ============================================================
  const extractCurrentLayoutConfig = useCallback(() => ({
    filters: {
      look_ahead_weeks: lookAheadWeeks ?? null,
      date_range: {
        from: dateRangeFilter?.from ? new Date(dateRangeFilter.from).toISOString() : null,
        to: dateRangeFilter?.to ? new Date(dateRangeFilter.to).toISOString() : null,
      },
      status: statusFilter || 'all',
      activity_search: activitySearch || '',
      critical_path_only: !!criticalPathOnly,
    },
    columns: { widths: columnWidths || {}, hidden: hiddenColumns || {} },
    timeline: { time_scale: timeScale || 'month' },
    layout: { schedule_width: scheduleWidth ?? 50 },
  }), [lookAheadWeeks, dateRangeFilter, statusFilter, activitySearch, criticalPathOnly,
       columnWidths, hiddenColumns, timeScale, scheduleWidth]);

  const applyLayoutConfig = useCallback((config) => {
    if (!config) return;
    const f = config.filters || {};
    setLookAheadWeeks(f.look_ahead_weeks ?? null);
    setDateRangeFilter({
      from: f.date_range?.from ? new Date(f.date_range.from) : null,
      to: f.date_range?.to ? new Date(f.date_range.to) : null,
    });
    setStatusFilter(f.status ?? 'all');
    setActivitySearch(f.activity_search ?? '');
    setCriticalPathOnly(!!f.critical_path_only);

    const c = config.columns || {};
    setColumnWidths({ ...LAYOUT_DEFAULT_COLUMN_WIDTHS, ...(c.widths || {}) });
    setHiddenColumns(c.hidden || {});

    setTimeScale(config.timeline?.time_scale ?? 'month');

    if (config.layout?.schedule_width != null) {
      setScheduleWidth(config.layout.schedule_width);
      try { localStorage.setItem('scheduleWidth', String(config.layout.schedule_width)); } catch {}
    }
  }, []);

  // Fetch templates once on mount
  useEffect(() => {
    let cancelled = false;
    api.get('/layout-templates')
      .then(res => { if (!cancelled) setLayoutTemplates(res.data || []); })
      .catch(err => console.error('Failed to fetch layout templates:', err));
    return () => { cancelled = true; };
  }, []);

  // Auto-apply default template once after templates load
  useEffect(() => {
    if (defaultTemplateApplied) return;
    if (!layoutTemplates.length) return;
    const def = layoutTemplates.find(t => t && t.is_default);
    if (def) {
      applyLayoutConfig(def.config);
      toast.success(`Applied default layout: ${def.name}`);
    }
    setDefaultTemplateApplied(true);
  }, [layoutTemplates, defaultTemplateApplied, applyLayoutConfig]);

  const handleApplyLayoutTemplate = (tpl) => {
    applyLayoutConfig(tpl.config);
    toast.success(`Applied "${tpl.name}"`);
  };

  const handleSaveLayoutTemplate = async (name, description, isDefault) => {
    try {
      const config = extractCurrentLayoutConfig();
      const { data } = await api.post('/layout-templates', {
        name, description, is_default: !!isDefault, config
      });
      // If this one became default, the backend demoted others; re-fetch full list
      if (isDefault) {
        const r = await api.get('/layout-templates');
        setLayoutTemplates(r.data || []);
      } else {
        setLayoutTemplates(prev => [data, ...prev]);
      }
      toast.success(`Saved "${name}"`);
    } catch (err) {
      toast.error('Failed to save template');
      throw err;
    }
  };

  const handleUpdateLayoutTemplate = async (id, patch) => {
    try {
      const { data } = await api.patch(`/layout-templates/${id}`, patch);
      // If is_default changed to true, refetch to get demoted siblings
      if (patch.is_default === true) {
        const r = await api.get('/layout-templates');
        setLayoutTemplates(r.data || []);
      } else {
        setLayoutTemplates(prev => prev.map(t => t.id === id ? data : t));
      }
      toast.success('Template updated');
    } catch {
      toast.error('Failed to update template');
    }
  };

  const handleDeleteLayoutTemplate = async (id) => {
    try {
      await api.delete(`/layout-templates/${id}`);
      setLayoutTemplates(prev => prev.filter(t => t.id !== id));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  // WBS paste handler
  const handlePasteWbs = async () => {
    // EDIT_STRUCTURE guard (defense-in-depth): copy/paste creates WBS structure.
    if (!can(Action.EDIT_STRUCTURE)) return;
    if (!clipboardWbs || !activeSchedule) return;
    setIsPasting(true);
    try {
      await api.post(`/wbs/${clipboardWbs.node.id}/copy`, {
        schedule_id: activeSchedule.id,
        include_activities: pasteIncludeActivities,
        // Collapse the disabled-state case at the wire so backend never sees
        // copy_relationships=true with include_activities=false (a no-op anyway).
        copy_relationships: pasteIncludeActivities && pasteCopyRelationships,
        target_parent_id: pasteTarget?.parent_id ?? null,
      });
      toast.success(`Pasted "${clipboardWbs.node.name}" successfully`);
      setShowPasteOptions(false);
      setRefreshKey(k => k + 1);
    } catch {
      toast.error('Failed to paste WBS — please try again');
    } finally {
      setIsPasting(false);
    }
  };

  // Activity clipboard paste — converts parsed TSV rows into POST /activities
  // calls. Targets the currently-selected WBS, falling back to a toast error
  // if no WBS context is available (we never want to silently dump pasted
  // rows into an unspecified bucket).
  const handlePasteActivities = useCallback(async (rows) => {
    // EDIT_ACTIVITY guard (defense-in-depth): pasting rows fires POST /activities.
    if (!can(Action.EDIT_ACTIVITY)) return;
    if (!activeSchedule) {
      toast.error('No active schedule — cannot paste');
      return;
    }
    const wbsId = selectedWbsNode?.id;
    if (!wbsId) {
      toast.error('Select a WBS row first to paste activities into');
      return;
    }

    const payloads = rows
      .map(parsedRowToActivity)
      .filter(Boolean)
      .map(p => ({ ...p, schedule_id: activeSchedule.id, wbs_id: wbsId }));

    if (payloads.length === 0) {
      toast.error('Pasted content has no usable activity rows');
      return;
    }

    let created = 0;
    let failed = 0;
    for (const payload of payloads) {
      try {
        await api.post('/activities', payload);
        created += 1;
      } catch {
        failed += 1;
      }
    }

    if (created > 0) {
      toast.success(`Pasted ${created} ${created === 1 ? 'activity' : 'activities'}${failed ? ` (${failed} failed)` : ''}`);
      setRefreshKey(k => k + 1);
    } else {
      toast.error('Paste failed — no activities were created');
    }
  }, [activeSchedule, selectedWbsNode, can]);

  // Toggle full screen mode
  const toggleFullScreen = () => {
    setIsFullScreen(prev => !prev);
  };
  
  // Handle synchronized scrolling between panels
  const handleScheduleScroll = useCallback((scrollTop) => {
    if (isScrollingSyncRef.current) return;
    isScrollingSyncRef.current = true;
    if (ganttScrollRef.current?.scrollTo) {
      ganttScrollRef.current.scrollTo(scrollTop);
    }
    setTimeout(() => { isScrollingSyncRef.current = false; }, 10);
  }, []);
  
  const handleGanttScroll = useCallback((scrollTop) => {
    if (isScrollingSyncRef.current) return;
    isScrollingSyncRef.current = true;
    if (scheduleScrollRef.current?.scrollTo) {
      scheduleScrollRef.current.scrollTo(scrollTop);
    }
    setTimeout(() => { isScrollingSyncRef.current = false; }, 10);
  }, []);
  
  // Derived: the currently-selected activity OBJECT, looked up from
  // allActivities by id. Stable across re-renders when the underlying
  // activity is unchanged (same ref) — which keeps the bottom panel's
  // useEffect [activityProp] from firing on every irrelevant re-render
  // (the prior cause of the calendar re-mount / bounce).
  const selectedActivity = useMemo(() => {
    if (!selectedActivityId) return null;
    return allActivities.find(a => a.id === selectedActivityId) || null;
  }, [selectedActivityId, allActivities]);

  const handleSelectActivity = (activity) => {
    setSelectedActivityId(activity?.id || null);
    if (activity) {
      setIsActivityPanelOpen(true);
    }
  };

  // Bulk comment counts for row badges — one aggregate per schedule.
  // refreshKey keeps badges honest after deletes/imports; CommentsPanel
  // keeps them live in between via handleCommentCountChange below.
  useEffect(() => {
    if (!activeSchedule?.id) {
      setCommentCounts({});
      return undefined;
    }
    let cancelled = false;
    api.get('/comments/counts', { params: { schedule_id: activeSchedule.id } })
      .then((res) => {
        if (!cancelled) setCommentCounts(res.data?.counts || {});
      })
      .catch(() => {
        // No READ access or transient failure — badges just stay empty.
      });
    return () => { cancelled = true; };
  }, [activeSchedule?.id, refreshKey]);

  const handleCommentCountChange = useCallback((entityId, count) => {
    setCommentCounts((prev) => ({ ...prev, [entityId]: count }));
  }, []);

  // Row badge click: select the activity and force the Comments tab open.
  const handleOpenComments = useCallback((activity) => {
    setSelectedActivityId(activity?.id || null);
    if (activity) setIsActivityPanelOpen(true);
    setOpenCommentsSignal((s) => s + 1);
  }, []);

  // Handle activity panel close
  const handleActivityPanelClose = () => {
    setIsActivityPanelOpen(false);
    setSelectedActivityId(null);
  };

  // Callback when WBS structure changes — no debounce. The backend's
  // synchronous CPM (Bug J fix) means there's nothing to wait for.
  const handleWbsChange = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Callback when activities change — same as above. The prior 500ms
  // setTimeout was the load-bearing cause of "bottom panel out of sync"
  // — the panel waited half a second extra to see the fresh data the
  // Gantt was already showing. Removing it sync's both surfaces.
  //
  // This is the BIG-HAMMER refresh: bumps refreshKey, every child re-fetches
  // the full schedule. Used for bulk operations (paste, undo bulk, scenario
  // switch, WBS edits) where computing the cascade server-side would be
  // overkill. For SINGLE-activity edits, prefer handleActivitiesMerged below
  // — it merges the cascade in place with no network round-trip.
  const handleActivityChange = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  // Layer 2 — surgical merge of an ActivityUpdateResponse into local state.
  // Caller passes [editedActivity, ...cascadedActivities] from the PUT
  // response. We merge into allActivities (preserving identity for
  // unchanged rows via mergeActivityList from Layer 7a) and DO NOT
  // bump refreshKey — no full re-fetch is needed because the response
  // already contained the full diff.
  const handleActivitiesMerged = useCallback((updates) => {
    if (!updates || updates.length === 0) return;
    setAllActivities(prev => {
      const byId = new Map(prev.map(a => [a.id, a]));
      for (const u of updates) {
        const existing = byId.get(u.id);
        // Merge — preserves client-side fields the server doesn't return.
        byId.set(u.id, existing ? { ...existing, ...u } : u);
      }
      return mergeActivityList(prev, Array.from(byId.values()));
    });
    // Separate signal from refreshKey so the Gantt (own fetch) re-syncs.
    setMergedRevision(prev => prev + 1);
  }, []);

  // Single delete handler — with activitiesByWbs derived from
  // allActivities (UnifiedScheduleView's useMemo), filtering this list
  // is the only mutation needed. The derivation propagates to the
  // table in the same React tick. mergedRevision bump tells the Gantt
  // (still has its own fetch) to re-sync. Plan zesty-coalescing-clarke.md.
  const handleActivityDeleted = useCallback((activityId) => {
    setAllActivities(prev => prev.filter(a => a.id !== activityId));
    setMergedRevision(prev => prev + 1);
  }, []);

  // Optimistic-update prop wired into UnifiedScheduleView. Child calls
  // with a (prev => next) updater; parent applies to allActivities and
  // the derivation propagates back down. Replaces the in-child
  // setActivitiesByWbs calls that the previous dual-store architecture
  // required.
  const handleActivitiesOptimistic = useCallback((updater) => {
    setAllActivities(prev => updater(prev));
    setMergedRevision(prev => prev + 1);
  }, []);

  // Called by UnifiedScheduleView after a recursive WBS delete returns its
  // cascade payload with the list of activity ids the backend removed.
  // Filters them out of allActivities (the source the Gantt + bottom panel
  // consume) so neither surface holds a ghost row. Tree-level refetch is
  // still triggered separately via onWbsChange → refreshKey bump.
  const handleActivitiesRemoved = useCallback((ids) => {
    if (!ids?.length) return;
    const idSet = new Set(ids);
    setAllActivities(prev => prev.filter(a => !idSet.has(a.id)));
    setMergedRevision(prev => prev + 1);
  }, []);

  // Handle mode change
  const handleModeChange = (newMode) => {
    setScheduleMode(newMode);
    if (newMode === 'normal') {
      setSelectedBaselineId(null);
      setSelectedScenarioId(null);
      setBaselineComparisonData(null);
      setScenarioData(null);
    }
    if (newMode === 'baseline') {
      setSelectedScenarioId(null);
      setScenarioData(null);
    }
    if (newMode === 'scenario') {
      setSelectedBaselineId(null);
      setBaselineComparisonData(null);
    }
  };

  // Handle baseline data load
  const handleBaselineDataLoad = (comparisonData) => {
    setBaselineComparisonData(comparisonData);
  };

  // Handle baseline columns data load (for schedule view, not baseline comparison mode)
  const handleBaselineColumnsDataLoad = (data) => {
    setBaselineColumnsData(data);
  };

  // Handle scenario data load
  const handleScenarioDataLoad = (data) => {
    setScenarioData(data);
    // Force refresh to reload data with new scenario
    setRefreshKey(prev => prev + 1);
  };

  // Handle new baseline creation
  const handleCreateBaseline = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Handle new scenario creation
  const handleCreateScenario = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Horizontal resize handlers (for side-by-side layout)
  const handleHorizontalResizeStart = (e) => {
    e.preventDefault();
    setIsResizingHorizontal(true);
    startXRef.current = e.clientX;
    startWidthRef.current = scheduleWidth;
  };

  const handleHorizontalResizeMove = useCallback((e) => {
    if (!isResizingHorizontal || !containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    const deltaX = e.clientX - startXRef.current;
    const deltaPercent = (deltaX / containerWidth) * 100;
    const newWidth = Math.max(20, Math.min(80, startWidthRef.current + deltaPercent));
    setScheduleWidth(newWidth);
    localStorage.setItem('scheduleWidth', Math.round(newWidth).toString());
  }, [isResizingHorizontal]);

  const handleHorizontalResizeEnd = useCallback(() => {
    setIsResizingHorizontal(false);
  }, []);

  useEffect(() => {
    if (isResizingHorizontal) {
      document.addEventListener('mousemove', handleHorizontalResizeMove);
      document.addEventListener('mouseup', handleHorizontalResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleHorizontalResizeMove);
      document.removeEventListener('mouseup', handleHorizontalResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingHorizontal, handleHorizontalResizeMove, handleHorizontalResizeEnd]);

  // Helper to format dates
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  // Calculate scenario comparison stats
  const getScenarioComparisonStats = () => {
    if (!scenarioData?.scenario_activities || !scenarioData?.current_activities) {
      return { changed: 0, unchanged: 0, durationDelta: 0 };
    }
    
    const currentMap = new Map(
      scenarioData.current_activities.map(a => [a.activity_id, a])
    );
    
    let changed = 0;
    let unchanged = 0;
    let totalDurationDelta = 0;
    
    scenarioData.scenario_activities.forEach(scenarioAct => {
      const currentAct = currentMap.get(scenarioAct.original_activity_id);
      if (currentAct) {
        const durationDiff = (scenarioAct.original_duration || 0) - (currentAct.original_duration || 0);
        if (durationDiff !== 0) {
          changed++;
          totalDurationDelta += durationDiff;
        } else {
          unchanged++;
        }
      }
    });
    
    return { changed, unchanged, durationDelta: totalDurationDelta };
  };

  // No project/schedule selected state
  if (!activeProject) {
    return (
      <div className="h-[calc(100vh-8rem)] flex flex-col items-center justify-center" data-testid="scheduling-screen-empty">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Building2 className="h-8 w-8 text-slate-300" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">No Project Selected</h2>
        <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
          Select a project from the header to view the scheduling screen with WBS structure, activities, and Gantt timeline.
        </p>
        <Badge variant="outline" className="text-xs">
          Use &quot;Select Project&quot; button in the header
        </Badge>
      </div>
    );
  }

  if (!activeSchedule) {
    return (
      <div className="h-[calc(100vh-8rem)] flex flex-col items-center justify-center" data-testid="scheduling-screen-no-schedule">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <CalendarDays className="h-8 w-8 text-slate-300" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">No Schedule Selected</h2>
        <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
          Create or select a schedule for &quot;{activeProject.name}&quot; to view the scheduling screen.
        </p>
        <Badge variant="outline" className="text-xs">
          Go to Schedules page to create one
        </Badge>
      </div>
    );
  }

  // Mobile (<=768px): render the dedicated Collaborator view instead of the
  // desktop split-pane (table + Gantt). All hooks above run unconditionally
  // (alongside the no-project / no-schedule guards), so the desktop return
  // below stays byte-identical and is simply unreachable here. See plan
  // shimmering-munching-stallman.md.
  if (isMobile) {
    return (
      <div
        className="flex flex-col h-[calc(100vh-4.0rem)] -m-6 overflow-hidden bg-white"
        data-testid="scheduling-screen-mobile"
      >
        <MobileScheduleView
          activities={visibleActivities}
          canEditActuals={can(Action.EDIT_ACTUALS)}
          projectDataDate={activeProject?.data_date}
          activeProject={activeProject}
          activeSchedule={activeSchedule}
          onActivitiesMerged={handleActivitiesMerged}
          onActivityChange={handleActivityChange}
        />
      </div>
    );
  }

  const scenarioStats = getScenarioComparisonStats();

  return (
    <div 
      ref={containerRef}
      className={`flex flex-col overflow-hidden ${
        isFullScreen 
          ? 'fixed inset-0 z-50 bg-white' 
          : 'h-[calc(100vh-4.0rem)] -m-6'
      }`}
      data-testid="scheduling-screen"
    >
      {/* Schedule Context Header */}
      <ScheduleHeader 
        scheduleMode={scheduleMode}
        onModeChange={handleModeChange}
        selectedBaselineId={selectedBaselineId}
        onBaselineChange={setSelectedBaselineId}
        onBaselineDataLoad={handleBaselineDataLoad}
        selectedScenarioId={selectedScenarioId}
        onScenarioChange={setSelectedScenarioId}
        onScenarioDataLoad={handleScenarioDataLoad}
        onCreateBaseline={handleCreateBaseline}
        onCreateScenario={handleCreateScenario}
        undoStack={undoStack}
        onUndo={handleGlobalUndo}
        isUndoing={isUndoing}
        selectedBaselineForColumns={selectedBaselineForColumns}
        onBaselineForColumnsChange={setSelectedBaselineForColumns}
        onBaselineColumnsDataLoad={handleBaselineColumnsDataLoad}
      />
      
      {/* Schedule Section Title Bar */}
      <div className="h-9 px-4 bg-slate-800 flex items-center gap-3 shrink-0" data-testid="schedule-section-header">
        <ListTree className="h-4 w-4 text-slate-300" />
        <span className="text-sm font-medium text-white tracking-wide">Schedule</span>
        <div className="h-4 w-px bg-slate-600" />
        <span className="text-xs text-slate-400">WBS & Activities (Unified View)</span>
        
        {/* Look-Ahead & Filter Controls */}
        <div className="flex items-center gap-2 ml-4">
          {/* Critical Path Filter */}
          <button
            onClick={() => setCriticalPathOnly(v => !v)}
            className={cn(
              "h-7 px-2.5 text-xs rounded border font-medium transition-colors whitespace-nowrap",
              criticalPathOnly
                ? "bg-red-500/20 border-red-400/50 text-red-300"
                : "bg-slate-700 border-slate-600 text-slate-300 hover:bg-slate-600 hover:text-white"
            )}
          >
            Critical Path
          </button>

          {/* Layout Templates Menu */}
          <LayoutTemplatesMenu
            templates={layoutTemplates}
            onApplyTemplate={handleApplyLayoutTemplate}
            onSaveCurrent={handleSaveLayoutTemplate}
            onUpdateTemplate={handleUpdateLayoutTemplate}
            onDeleteTemplate={handleDeleteLayoutTemplate}
          />

          {/* Look-Ahead Filter */}
          <div className="flex items-center">
            <Select 
              value={lookAheadWeeks?.toString() || "all"} 
              onValueChange={(value) => setLookAheadWeeks(value === "all" ? null : parseInt(value))}
            >
              <SelectTrigger className="h-7 w-[130px] bg-slate-700 border-slate-600 text-white text-xs">
                <Eye className="h-3 w-3 mr-1" />
                <SelectValue placeholder="Look-Ahead" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activities</SelectItem>
                <SelectItem value="3">3-Week Look-Ahead</SelectItem>
                <SelectItem value="6">6-Week Look-Ahead</SelectItem>
                <SelectItem value="12">12-Week Look-Ahead</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Status Filter */}
          <Select 
            value={statusFilter} 
            onValueChange={setStatusFilter}
          >
            <SelectTrigger className="h-7 w-[110px] bg-slate-700 border-slate-600 text-white text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="not_started">Not Started</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className={`h-7 text-xs gap-1 ${
                  dateRangeFilter.from || dateRangeFilter.to 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white'
                }`}
              >
                <Calendar className="h-3 w-3" />
                {dateRangeFilter.from || dateRangeFilter.to ? (
                  <span>
                    {dateRangeFilter.from ? format(dateRangeFilter.from, 'MMM d') : '...'} - {dateRangeFilter.to ? format(dateRangeFilter.to, 'MMM d') : '...'}
                  </span>
                ) : (
                  <span>Date Range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3 border-b">
                <p className="text-sm font-medium">Filter by Date Range</p>
                <p className="text-xs text-slate-500">Select start and end dates</p>
              </div>
              <CalendarPicker
                mode="range"
                selected={{ from: dateRangeFilter.from, to: dateRangeFilter.to }}
                onSelect={(range) => setDateRangeFilter({ from: range?.from || null, to: range?.to || null })}
                numberOfMonths={2}
                className="p-3"
              />
              <div className="p-2 border-t flex justify-between">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setDateRangeFilter({ from: null, to: null })}
                  className="h-7 text-xs"
                >
                  Clear
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  className="h-7 text-xs"
                >
                  Apply
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Clear All Filters */}
          {(lookAheadWeeks || statusFilter !== 'all' || dateRangeFilter.from || dateRangeFilter.to || activitySearch || criticalPathOnly) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                    onClick={() => {
                      setLookAheadWeeks(null);
                      setStatusFilter('all');
                      setDateRangeFilter({ from: null, to: null });
                      setActivitySearch('');
                      setCriticalPathOnly(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p>Clear All Filters</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        {/* Active Filter Indicator */}
        {(lookAheadWeeks || statusFilter !== 'all' || dateRangeFilter.from || activitySearch || criticalPathOnly) && (
          <Badge className="bg-blue-500/20 text-blue-300 border-blue-400/30 text-[10px] gap-1">
            <Filter className="h-3 w-3" />
            Filtered View
          </Badge>
        )}
        
        {/* Selected WBS — Copy button (plan-level: paste creates structure).
            Gated on EDIT_STRUCTURE so view/collaborate never see a copy affordance. */}
        {selectedWbsNode && can(Action.EDIT_STRUCTURE) && (
          <div className="flex items-center gap-1.5 text-xs text-amber-200 bg-amber-500/10 px-2 py-1 rounded border border-amber-400/30">
            <span className="text-[10px] uppercase tracking-wide opacity-70">Selected:</span>
            <span className="font-medium max-w-[150px] truncate">{selectedWbsNode.name}</span>
            <button
              onClick={() => {
                setClipboardWbs({ node: selectedWbsNode });
                toast.success(`Copied "${selectedWbsNode.name}" — click Paste or Ctrl+V`);
              }}
              className="ml-1 flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 hover:bg-amber-500/40 rounded text-amber-100 hover:text-white transition-colors"
              title="Copy this WBS (Ctrl+C)"
            >
              <Copy className="h-3 w-3" />
              Copy
            </button>
          </div>
        )}

        {/* WBS Clipboard Indicator — Paste button. Gated on EDIT_STRUCTURE (plan). */}
        {clipboardWbs && can(Action.EDIT_STRUCTURE) && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-200 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-400/30">
            <ClipboardPaste className="h-3 w-3" />
            <span className="max-w-[120px] truncate">"{clipboardWbs.node.name}"</span>
            <button
              onClick={() => {
                setPasteTarget(selectedWbsNode);
                setPasteIncludeActivities(true);
                setPasteCopyRelationships(true);
                setShowPasteOptions(true);
              }}
              className="ml-1 flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 hover:bg-emerald-500/40 rounded text-emerald-100 hover:text-white transition-colors"
              title="Paste WBS (Ctrl+V)"
            >
              <ClipboardPaste className="h-3 w-3" />
              Paste
            </button>
            <button
              onClick={() => setClipboardWbs(null)}
              className="ml-0.5 hover:text-white"
              title="Clear clipboard"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* AI Assistant Button — the assistant APPLIES schedule changes
            (approve -> live structure mutation), so it is plan-level work.
            Gate the trigger on MANAGE_SCHEDULES so view/collaborate never see
            an apply control the backend would 403 (hide contract). The
            component also self-gates (defense-in-depth). */}
        {activeSchedule && can(Action.MANAGE_SCHEDULES) && (
          <AIScheduleAssistant
            scheduleId={activeSchedule.id}
            scheduleName={activeSchedule.name}
            onChangesApplied={() => setRefreshKey(prev => prev + 1)}
          />
        )}
        
        {/* Full Screen Toggle */}
        <div className="ml-auto flex items-center gap-1">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${isFullScreen ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                  onClick={toggleFullScreen}
                  data-testid="layout-fullscreen"
                >
                  {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                <p>{isFullScreen ? 'Exit Full Screen (ESC)' : 'Full Screen Mode'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {scheduleMode === 'scenario' && selectedScenarioId && (
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 text-[10px]">
            What-If Scenario Active
          </Badge>
        )}
        {selectedBaselineForColumns && (
          <Badge className="bg-purple-500/20 text-purple-300 border-purple-400/30 text-[10px]">
            Comparing with Baseline
          </Badge>
        )}
      </div>
      
      {/* Main Schedule Content Area */}
      <div className="flex-1 flex flex-col min-h-0 bg-white relative overflow-hidden">
        {/* Scenario Comparison Panel */}
        {scheduleMode === 'scenario' && scenarioData && (
          <div className="border-b border-amber-200 bg-amber-50/50" data-testid="scenario-comparison-panel">
            <div className="px-4 py-2 flex items-center gap-6 border-b border-amber-100">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-medium text-amber-700">
                  Scenario: {scenarioData.name}
                </span>
                <Badge variant="outline" className="text-[10px] bg-white border-amber-200 text-amber-600">
                  {scenarioData.status || 'draft'}
                </Badge>
              </div>
              
              <div className="h-4 w-px bg-amber-200" />
              
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-slate-600">Activities:</span>
                  <span className="font-medium text-amber-700">{scenarioData.scenario_activities?.length || 0}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-slate-600">Modified:</span>
                  <span className="font-medium text-blue-600">{scenarioStats.changed}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Minus className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-slate-600">Unchanged:</span>
                  <span className="font-medium text-slate-600">{scenarioStats.unchanged}</span>
                </div>
                {scenarioStats.durationDelta !== 0 && (
                  <div className="flex items-center gap-1.5">
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-slate-600">Duration Δ:</span>
                    <span className={`font-medium ${scenarioStats.durationDelta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {scenarioStats.durationDelta > 0 ? '+' : ''}{scenarioStats.durationDelta}d
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Scenario Activities Comparison Table */}
            {scenarioData.scenario_activities && scenarioData.scenario_activities.length > 0 && (
              <ScrollArea className="max-h-[200px]">
                <div className="px-4 py-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-amber-100">
                        <th className="text-left py-1 px-2 font-medium text-amber-600">Activity</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Live Duration</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Scenario Duration</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Live Start</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Scenario Start</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Live Finish</th>
                        <th className="text-center py-1 px-2 font-medium text-amber-600">Scenario Finish</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scenarioData.scenario_activities.slice(0, 10).map((scenarioAct) => {
                        const currentAct = scenarioData.current_activities?.find(
                          a => a.activity_id === scenarioAct.original_activity_id
                        );
                        const durationChanged = currentAct && 
                          scenarioAct.original_duration !== currentAct.original_duration;
                        
                        return (
                          <tr key={scenarioAct.id} className={`border-b border-amber-50 ${durationChanged ? 'bg-amber-50/50' : ''}`}>
                            <td className="py-1.5 px-2">
                              <span className="font-mono text-slate-500">{scenarioAct.original_activity_id}</span>
                              <span className="ml-2 text-slate-700">{scenarioAct.name}</span>
                              {durationChanged && (
                                <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-600 border-amber-200">Modified</Badge>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-center text-slate-600 tabular-nums">
                              {currentAct?.original_duration || '—'}d
                            </td>
                            <td className={`py-1.5 px-2 text-center tabular-nums font-medium ${
                              durationChanged ? 'text-amber-700' : 'text-slate-600'
                            }`}>
                              {scenarioAct.original_duration}d
                            </td>
                            <td className="py-1.5 px-2 text-center text-slate-600 tabular-nums">
                              {formatDate(currentAct?.planned_start || currentAct?.early_start)}
                            </td>
                            <td className="py-1.5 px-2 text-center text-slate-600 tabular-nums">
                              {formatDate(scenarioAct.planned_start || scenarioAct.early_start)}
                            </td>
                            <td className="py-1.5 px-2 text-center text-slate-600 tabular-nums">
                              {formatDate(currentAct?.planned_finish || currentAct?.early_finish)}
                            </td>
                            <td className="py-1.5 px-2 text-center text-slate-600 tabular-nums">
                              {formatDate(scenarioAct.planned_finish || scenarioAct.early_finish)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {scenarioData.scenario_activities.length > 10 && (
                    <p className="text-[10px] text-amber-500 text-center py-2">
                      Showing 10 of {scenarioData.scenario_activities.length} activities
                    </p>
                  )}
                </div>
              </ScrollArea>
            )}
            
            {/* Scenario Description */}
            {scenarioData.description && (
              <div className="px-4 py-2 border-t border-amber-100">
                <p className="text-xs text-amber-700">
                  <span className="font-medium">Description:</span> {scenarioData.description}
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Layout-aware content area */}
          {/* Side-by-Side Layout */}
          <div className="flex-1 flex min-h-[250px]" data-testid="layout-container-side-by-side">
            {/* Schedule View (Left) */}
            <div 
              className="min-w-0 h-full"
              style={{ width: `${scheduleWidth}%` }}
            >
              <UnifiedScheduleView
                refreshKey={refreshKey}
                activities={visibleActivities}
                externalSelectedActivityId={selectedActivityId}
                onSelectActivity={handleSelectActivity}
                onActivityChange={handleActivityChange}
                onActivitiesMerged={handleActivitiesMerged}
                onActivitiesOptimistic={handleActivitiesOptimistic}
                onWbsChange={handleWbsChange}
                baselineComparisonData={null}
                baselineColumnsData={baselineColumnsData}
                selectedBaselineForColumns={selectedBaselineForColumns}
                scenarioData={scheduleMode === 'scenario' ? scenarioData : null}
                onVerticalScroll={handleScheduleScroll}
                scrollRef={scheduleScrollRef}
                lookAheadWeeks={lookAheadWeeks}
                statusFilter={statusFilter}
                dateRangeFilter={dateRangeFilter}
                activitySearch={activitySearch}
                onActivitySearchChange={setActivitySearch}
                criticalPathOnly={criticalPathOnly}
                expandedNodes={sharedExpandedNodes}
                onExpandChange={setSharedExpandedNodes}
                undoStack={undoStack}
                setUndoStack={setUndoStack}
                layoutMode={layoutMode}
                onSelectWbs={(node) => setSelectedWbsNode(node)}
                clipboardWbs={clipboardWbs}
                onCopyWbs={(node) => {
                  setClipboardWbs({ node });
                  toast.success(`Copied "${node.name}" — Ctrl+V or click Paste`);
                }}
                onPasteWbs={(targetNode) => {
                  setPasteTarget(targetNode);
                  setPasteIncludeActivities(true);
                  setShowPasteOptions(true);
                }}
                onPasteActivities={handlePasteActivities}
                columnWidths={columnWidths}
                onColumnWidthsChange={setColumnWidths}
                hiddenColumns={hiddenColumns}
                onHiddenColumnsChange={setHiddenColumns}
                onActivityDeleted={handleActivityDeleted}
                onActivitiesRemoved={handleActivitiesRemoved}
                commentCounts={commentCounts}
                onOpenComments={handleOpenComments}
                onCommentCountChange={handleCommentCountChange}
              />
            </div>
            
            {/* Horizontal Resizable Divider */}
            <div 
              className={`
                w-2 bg-slate-100 border-x border-slate-200 cursor-col-resize 
                flex items-center justify-center group hover:bg-slate-200 transition-colors
                ${isResizingHorizontal ? 'bg-blue-100 border-blue-300' : ''}
              `}
              onMouseDown={handleHorizontalResizeStart}
              data-testid="resize-handle-horizontal"
            >
              <div className={`
                flex flex-col items-center gap-0.5 py-4 px-0.5 rounded transition-colors
                ${isResizingHorizontal ? 'bg-blue-200' : 'group-hover:bg-slate-300'}
              `}>
                <GripVertical className={`h-3 w-3 ${isResizingHorizontal ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`} />
              </div>
            </div>
            
            {/* Gantt Timeline (Right) */}
            <div 
              className="min-w-0 h-full relative overflow-hidden"
              style={{ width: `${100 - scheduleWidth}%` }}
            >
              <GanttTimeline
                refreshKey={refreshKey}
                mergedRevision={mergedRevision}
                selectedActivityId={selectedActivity?.id}
                onSelectActivity={handleSelectActivity}
                baselineColumnsData={baselineColumnsData}
                scenarioData={scheduleMode === 'scenario' ? scenarioData : null}
                onVerticalScroll={handleGanttScroll}
                scrollRef={ganttScrollRef}
                lookAheadWeeks={lookAheadWeeks}
                statusFilter={statusFilter}
                dateRangeFilter={dateRangeFilter}
                activitySearch={activitySearch}
                criticalPathOnly={criticalPathOnly}
                expandedNodes={sharedExpandedNodes}
                onExpandChange={setSharedExpandedNodes}
                timeScale={timeScale}
                onTimeScaleChange={setTimeScale}
              />
            </div>
          </div>
        
        {/* Activity Bottom Panel (Resizable) */}
        <ActivityBottomPanel
          activity={selectedActivity}
          isOpen={isActivityPanelOpen}
          onClose={handleActivityPanelClose}
          onActivityUpdate={() => setRefreshKey(prev => prev + 1)}
          onActivitiesMerged={handleActivitiesMerged}
          onActivityDeleted={handleActivityDeleted}
          resources={projectResources}
          calendars={projectCalendars}
          scheduleId={activeSchedule?.id}
          canEdit={can(Action.EDIT_ACTIVITY)}
          canEditActuals={can(Action.EDIT_ACTUALS)}
          undoStack={undoStack}
          setUndoStack={setUndoStack}
          allActivities={allActivities}
          projectDataDate={activeProject?.data_date}
          openCommentsSignal={openCommentsSignal}
          onCommentCountChange={handleCommentCountChange}
        />
      </div>

      {/* WBS Paste Options Dialog */}
      <Dialog open={showPasteOptions} onOpenChange={(open) => !open && setShowPasteOptions(false)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-4 w-4 text-emerald-600" />
              Paste WBS
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Pasting <strong>"{clipboardWbs?.node?.name}"</strong>
              {pasteTarget
                ? ` alongside "${pasteTarget.name}"`
                : ' at root level'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-3 space-y-3">
            <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-md">
              <Checkbox
                id="includeActivities"
                checked={pasteIncludeActivities}
                onCheckedChange={(checked) => setPasteIncludeActivities(!!checked)}
              />
              <div>
                <label htmlFor="includeActivities" className="text-sm font-medium cursor-pointer">
                  Include activities
                </label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Copy all activities under the WBS structure
                </p>
              </div>
            </div>
            <div
              className={`flex items-center gap-3 p-3 border border-slate-200 rounded-md ${
                pasteIncludeActivities ? '' : 'opacity-50'
              }`}
            >
              <Checkbox
                id="copyRelationships"
                checked={pasteIncludeActivities && pasteCopyRelationships}
                disabled={!pasteIncludeActivities}
                onCheckedChange={(checked) => setPasteCopyRelationships(!!checked)}
              />
              <div>
                <label
                  htmlFor="copyRelationships"
                  className={`text-sm font-medium ${
                    pasteIncludeActivities ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                >
                  Copy relationships
                </label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Duplicate internal predecessor/successor links between copied activities
                </p>
              </div>
            </div>
            {!pasteIncludeActivities && (
              <p className="text-xs text-slate-400 px-1">
                Only the WBS hierarchy will be copied — no activities, no relationships.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowPasteOptions(false)} disabled={isPasting}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handlePasteWbs}
              disabled={isPasting}
            >
              {isPasting ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Pasting…</>
              ) : (
                <><ClipboardPaste className="h-4 w-4 mr-1.5" /> Paste</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulingScreen;
